import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api/client";
import { fmtMoney } from "../format";
import { MeResponse } from "../types";
import { DealCard } from "./DealCard";

interface CashPositionRow {
  account: string;
  payment_date: string;
  currency: string;
  incoming: number;
  outgoing: number;
  net: number;
}

interface CashPositionDetail {
  deal_id: number;
  payment_id: number;
  payment_date: string;
  payment_type: string;
  direction: string | null;
  status: string;
  side: "in" | "out";
  amount: number;
  currency: string;
}

interface CashPositionResponse {
  account: string;
  currencies: string[];
  dates: string[];
  statuses: string[];
  rows: CashPositionRow[];
  status_tables: Array<{
    status: string;
    rows: CashPositionRow[];
  }>;
  details: CashPositionDetail[];
}

const PAYMENT_TYPE_LABEL: Record<string, string> = {
  remittance: "Перевод",
  notional: "Тело платежа",
  fees: "Комиссии",
  body_plus_fee: "Тело + комиссия",
  return: "Возврат",
  test: "Тестовый перевод",
};

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  review: "На проверке",
  sent: "Отправлен",
  aml_package_required: "AML-пакет",
  received: "Получен",
  returned: "Возвращён",
  cancelled: "Отменён",
};

function csvCell(value: string | number | null | undefined): string {
  const text = value === null || value === undefined ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadCsv(details: CashPositionDetail[]) {
  const headers = [
    "deal_id",
    "payment_id",
    "payment_date",
    "payment_type",
    "status",
    "side",
    "amount",
    "currency",
    "direction",
  ];
  const rows = details.map((item) => [
    `D-${String(item.deal_id).padStart(6, "0")}`,
    item.payment_id,
    item.payment_date,
    PAYMENT_TYPE_LABEL[item.payment_type] ?? item.payment_type,
    PAYMENT_STATUS_LABEL[item.status] ?? item.status,
    item.side === "in" ? "in" : "out",
    item.amount,
    item.currency,
    item.direction ?? "",
  ]);
  const csv = [
    headers.map(csvCell).join(","),
    ...rows.map((row) => row.map(csvCell).join(",")),
  ].join("\r\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "test_cash_position_payments.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function PivotTable({ rows }: { rows: CashPositionRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[620px] text-sm">
        <thead className="bg-tg-bg text-tg-hint">
          <tr>
            <th className="text-left font-medium px-3 py-2">Валюта</th>
            <th className="text-left font-medium px-3 py-2">Дата</th>
            <th className="text-right font-medium px-3 py-2">Вход</th>
            <th className="text-right font-medium px-3 py-2">Выход</th>
            <th className="text-right font-medium px-3 py-2">Нетто</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-tg-secondaryBg">
          {rows.map((row) => (
            <tr key={`${row.payment_date}-${row.currency}`}>
              <td className="px-3 py-2 font-semibold">{row.currency}</td>
              <td className="px-3 py-2 whitespace-nowrap">
                {row.payment_date}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {fmtMoney(row.incoming)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {fmtMoney(row.outgoing)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold">
                {fmtMoney(row.net)}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} className="px-3 py-6 text-center text-tg-hint">
                Нет платежей по INTERNAL_ACCOUNT.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function TestCashPositionPage({ me: _me }: { me: MeResponse }) {
  const [data, setData] = useState<CashPositionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [openDealId, setOpenDealId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .get<CashPositionResponse>("/api/cash-position/test")
      .then(setData)
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const statusTables = useMemo(() => data?.status_tables ?? [], [data]);
  const recentPayments = useMemo(
    () => (data?.details ?? []).slice().reverse().slice(0, 24),
    [data],
  );

  if (loading && !data) {
    return <div className="text-tg-hint animate-pulse">Загрузка...</div>;
  }

  if (openDealId !== null) {
    return (
      <DealCard
        dealId={openDealId}
        me={_me}
        onClose={() => setOpenDealId(null)}
        onChanged={load}
        onDeleted={() => {
          setOpenDealId(null);
          load();
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">(тест) кассовая позиция</h1>
          <div className="text-xs text-tg-hint mt-1">
            {data?.account ?? "INTERNAL_ACCOUNT"}
          </div>
        </div>
        <button
          onClick={load}
          className="px-3 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
        >
          Обновить
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {statusTables.map((table) => (
        <section
          key={table.status}
          className="bg-tg-sectionBg rounded-lg overflow-hidden border border-tg-secondaryBg"
        >
          <div className="px-3 py-2 bg-tg-bg border-b border-tg-secondaryBg text-sm font-semibold">
            {PAYMENT_STATUS_LABEL[table.status] ?? table.status}
          </div>
          <PivotTable rows={table.rows} />
        </section>
      ))}

      <section>
        <div className="flex items-center justify-between gap-3 mb-2">
          <h2 className="text-sm font-semibold">Последние платежи</h2>
          <button
            type="button"
            onClick={() => downloadCsv(data?.details ?? [])}
            disabled={(data?.details ?? []).length === 0}
            className="px-3 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-xs disabled:opacity-50"
          >
            CSV
          </button>
        </div>
        <div className="bg-tg-sectionBg rounded-lg divide-y divide-tg-secondaryBg overflow-hidden">
          {recentPayments.map((item) => (
            <button
              type="button"
              key={item.payment_id}
              onClick={() => setOpenDealId(item.deal_id)}
              className="w-full px-3 py-2 flex items-center justify-between gap-3 text-sm text-left hover:bg-tg-bg focus:outline-none focus:bg-tg-bg"
            >
              <div className="min-w-0">
                <div className="font-medium truncate">
                  D-{String(item.deal_id).padStart(6, "0")} ·{" "}
                  {PAYMENT_TYPE_LABEL[item.payment_type] ?? item.payment_type}
                </div>
                <div className="text-xs text-tg-hint">
                  {item.payment_date} ·{" "}
                  {PAYMENT_STATUS_LABEL[item.status] ?? item.status} ·{" "}
                  {item.side === "in" ? "Поступление" : "Списание"}
                </div>
              </div>
              <div className="text-right tabular-nums shrink-0">
                {item.side === "out" ? "-" : "+"}
                {fmtMoney(item.amount)} {item.currency}
              </div>
            </button>
          ))}
          {recentPayments.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-tg-hint">
              Нет платежей по INTERNAL_ACCOUNT.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
